import React, { useState, useEffect, useRef } from 'react';
import './Parcours.css';

const Parcours = ({ isVisible, onClose }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  
  const containerRef = useRef(null);
  const particlesRef = useRef(null);
  const timelineRef = useRef(null);
  
  // Variables pour les particules
  const numParticles = 60;
  const particles = useRef([]);
  
  // Données de la timeline
  const timelineEvents = [
    {
      year: '2025',
      title: 'En recherche active : Votre futur développeur Full Stack passionné',
      description: [
        'À la recherche d\'une opportunité de stage de 3 mois en développement web',
        'Prêt à apporter mon expertise technique et ma créativité à votre équipe',
        'Maîtrise des dernières technologies web et des méthodologies agiles',
        'Objectif : Contribuer à des projets innovants tout en continuant à développer mes compétences'
      ],
      inverted: false,
      icon: 'fas fa-search'
    },
    {
      year: '2024',
      title: 'Obtention du DEUST Webmaster & Métiers de l\'Internet',
      description: [
        'Validation du diplôme au Campus Virtuel TIC, Université de Limoges',
        'Maîtrise des technologies web et des métiers de l\'Internet',
        'Développement de projets web complets'
      ],
      inverted: true,
      icon: 'fas fa-graduation-cap'
    },
    {
      year: '2023',
      title: 'Stage Full Stack chez InSiliBio : Migration et optimisation du site web',
      description: [
        'Migration complète du site WordPress vers une architecture personnalisée',
        'Développement d\'une interface d\'administration intuitive',
        'Optimisation des performances et de la vitesse de chargement',
        'Rédaction du cahier des charges et documentation technique',
        'Gestion autonome du projet de A à Z (insilibio.com)'
      ],
      inverted: false,
      icon: 'fas fa-laptop-code'
    },
    {
      year: '2022',
      title: 'Stage professionnel chez AxYus : un tremplin pour ma carrière',
      description: [
        'Participation au développement de la plateforme MDNA (Mes Démarches Nouvelle-Aquitaine)',
        'Réalisation de la partie responsive de la page d\'accueil',
        'Conception de l\'interface d\'actualités',
        'Obtention de certificats en HTML, CSS, JavaScript, Java',
        'Obtention du badge LinkedIn en HTML/CSS'
      ],
      inverted: true,
      icon: 'fas fa-briefcase'
    },
    {
      year: '2021',
      title: 'Du lycée à l\'université : mes premiers pas dans le monde du web',
      description: [
        'Première année de DEUST Webmaster & Métiers de l\'Internet au Campus Virtuel TIC de l\'Université de Limoges',
        'Dessin à partir de code HTML/CSS/JavaScript'
      ],
      inverted: false,
      icon: 'fas fa-university'
    },
    {
      year: '2020',
      title: 'Des maths aux langages de programmation, j\'ai tout pour réussir!',
      description: [
        'Obtention du Baccalauréat général en spécialité Mathématiques et Physique Chimie au Lycée Bernard Palissy de GIEN',
        'Création de programmes en Python'
      ],
      inverted: true,
      icon: 'fas fa-calculator'
    }
  ];

  // Initialisation des particules
  useEffect(() => {
    if (isVisible && particlesRef.current) {
      for (let i = 0; i < numParticles; i++) {
        particles.current.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
      
      const canvas = particlesRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      animateParticles();
    }
    
    // Nettoyage
    return () => {
      particles.current = [];
    };
  }, [isVisible]);
  
  // Animation des particules
  const animateParticles = () => {
    if (!particlesRef.current || !isVisible) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.current.forEach(particle => {
      // Mouvement
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      
      // Rebond sur les bords
      if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      
      // Dessin de la particule
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
      ctx.fill();
      
      // Connexion entre particules proches
      particles.current.forEach(otherParticle => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });
    });
    
    requestAnimationFrame(animateParticles);
  };
  
  // Effet de suivi du curseur
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };
    
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
      // Animation d'entrée
      setTimeout(() => setIsLoaded(true), 300);
    } else {
      setIsLoaded(false);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible]);
  
  // Observer pour animer les éléments au défilement
  useEffect(() => {
    if (isVisible && timelineRef.current) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => [...prev, parseInt(entry.target.dataset.index)]);
          }
        });
      }, { threshold: 0.2 });
      
      const items = timelineRef.current.querySelectorAll('.timeline-item');
      items.forEach(item => {
        observer.observe(item);
      });
      
      return () => {
        items.forEach(item => observer.unobserve(item));
      };
    }
    
    return () => {
      setVisibleItems([]);
    };
  }, [isVisible]);
  
  // Définition de la position du gradient qui suit la souris
  const gradientPosition = `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(179, 135, 246, 0.2) 0%, rgba(92, 122, 250, 0.1) 40%, transparent 70%)`;

  return (
    <div 
      className={`parcours-section ${isVisible ? 'visible' : ''}`}
      aria-hidden={!isVisible}
      ref={containerRef}
    >
      {/* Effet de particules en arrière-plan */}
      <canvas 
        ref={particlesRef} 
        className="particles-canvas"
      />
      
      {/* Gradient qui suit la souris */}
      <div 
        className="mouse-gradient"
        style={{ background: gradientPosition }}
      />
      
      {/* Bouton de fermeture */}
      <div 
        className="parcours-close" 
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
        role="button"
        aria-label="Fermer la section Parcours"
        tabIndex={isVisible ? 0 : -1}
      >
        <span></span>
        <span></span>
      </div>
      
      <div className={`parcours-container ${isLoaded ? 'loaded' : ''}`}>
        <div className="parcours-header animated-element">
          <h2 className="parcours-title">Parcours</h2>
          <p className="parcours-subtitle">
            <span>Mon parcours de développement : une histoire de passion, de créativité et de croissance professionnelle.</span>
          </p>
        </div>
        
        <div className="timeline-container" ref={timelineRef}>
          <div className="timeline-line"></div>
          
          {timelineEvents.map((event, index) => (
            <div 
              key={index}
              className={`timeline-item ${event.inverted ? 'inverted' : ''} ${visibleItems.includes(index) ? 'visible' : ''}`}
              data-index={index}
            >
              <div className="timeline-dot">
                <i className={event.icon}></i>
              </div>
              
              <div className="timeline-content">
                <div className="timeline-year">{event.year}</div>
                <h3 className="timeline-title">{event.title}</h3>
                <ul className="timeline-description">
                  {event.description.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
          
          <div className={`timeline-end ${visibleItems.includes(timelineEvents.length) ? 'visible' : ''}`} data-index={timelineEvents.length}>
            <div className="timeline-end-dot">
              <i className="fas fa-rocket"></i>
            </div>
            <div className="timeline-end-content">
              <h4>Ensemble,<br/>écrivons notre<br/>histoire!</h4>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Parcours;